import { Component, OnInit, inject } from '@angular/core';
import { Router, ActivatedRoute } from '@angular/router';
import { KeycloakService } from 'keycloak-angular';
import { RolesApiService } from '@angular-workspace/services';
import { ToasterService } from '../../../services/toaster.service';

/**
 * Component that redirects users to their highest role panel
 * Used for panel root routes (e.g., /consumer, /provider, /org-admin, /admin)
 */
@Component({
  selector: 'app-role-redirect',
  standalone: true,
  template: '<div>Redirecting...</div>',
})
export class RoleRedirectComponent implements OnInit {
  private router = inject(Router);
  private route = inject(ActivatedRoute);
  private keycloak = inject(KeycloakService);
  private rolesService = inject(RolesApiService);
  private toasterService = inject(ToasterService);

  async ngOnInit(): Promise<void> {
    try {
      // Check if user is logged in
      const isLoggedIn = await this.keycloak.isLoggedIn();
      
      if (!isLoggedIn) {
        // If not logged in, redirect to home/landing page
        this.router.navigate(['/']);
        return;
      }

      // Get the current route to determine which panel the user is trying to access
      const currentUrl = this.router.url;
      const requestedPanel = this.getRequestedPanel(currentUrl);

      // Try to get user roles
      try {
        await this.rolesService.getRoles();
        const userRoles = this.rolesService.getUserRoles();

        if (userRoles && userRoles.length > 0) {
          // Get the panel route based on highest role
          const highestRolePanelRoute = this.rolesService.getHighestRolePanelRoute(userRoles);
          
          // Check if the requested panel matches the user's highest role panel
          if (requestedPanel && !highestRolePanelRoute.startsWith(`/${requestedPanel}`)) {
            // User doesn't have access to the requested panel, redirect to home
            console.log(`User doesn't have access to ${requestedPanel} panel, redirecting to home`);
            this.toasterService.warning("You don't have access to some of the panels.");
            this.router.navigate(['/'], { replaceUrl: true });
            return;
          }
          
          // User has access or no specific panel requested, redirect to their highest role panel
          this.router.navigate([highestRolePanelRoute], { replaceUrl: true });
          return;
        }
      } catch (error) {
        console.error('Error fetching roles for redirect:', error);
      }

      // Fallback: if roles can't be fetched, redirect to home
      this.toasterService.warning("You don't have access to some of the panels.");
      this.router.navigate(['/'], { replaceUrl: true });
    } catch (error) {
      console.error('Error in role redirect component:', error);
      // Fallback to home on error
      this.toasterService.warning("You don't have access to some of the panels.");
      this.router.navigate(['/'], { replaceUrl: true });
    }
  }

  /**
   * Extract the panel name from the current URL
   * @param url Current router URL
   * @returns Panel name (consumer, provider, org-admin, admin) or null
   */
  private getRequestedPanel(url: string): string | null {
    if (url.startsWith('/consumer')) {
      return 'consumer';
    } else if (url.startsWith('/provider')) {
      return 'provider';
    } else if (url.startsWith('/org-admin')) {
      return 'org-admin';
    } else if (url.startsWith('/admin')) {
      return 'admin';
    }
    return null;
  }
}

